import axios from "../../../apis/Api";
import fileSaver from "file-saver";
import {
  GET_SCR_V2_COMPLETE_DETAIL,
  GET_SCR_V2_REPORT_LIST,
  GET_SCR_V2_SUMMARY_REPORT,
} from "../../../Types";
import { toast } from "react-toastify";
import { showLoading, hideLoading } from "react-redux-loading-bar";
import {
  extractScrV2ApiResponse as extractApiResponse,
  handleScrV2RequestError as handleRequestError,
  pickScrV2Value as pickValue,
} from "./SCRV2RequestHelpers";
import { pickScrV2IdentityFields } from "./SCRV2IdentityFields";

let reportListAbortController = null;
let summaryReportAbortController = null;
let detailReportAbortController = null;
let summaryReportActiveScrId = null;
let detailReportActiveScrId = null;
const reportListCache = new Map();
const summaryReportCache = new Map();
const detailReportCache = new Map();
const detailReportInFlight = new Map();
const invoiceDetailCache = new Map();
const invoiceDetailInFlight = new Map();
const REPORT_LIST_CACHE_TTL_MS = 30_000;
const SUMMARY_REPORT_LIVE_CACHE_TTL_MS = 30_000;
const SUMMARY_REPORT_FINALIZED_CACHE_TTL_MS = 10 * 60 * 1000;
const DETAIL_REPORT_LIVE_CACHE_TTL_MS = 30_000;
const DETAIL_REPORT_FINALIZED_CACHE_TTL_MS = 10 * 60 * 1000;
const INVOICE_DETAIL_LIVE_CACHE_TTL_MS = 30_000;
const INVOICE_DETAIL_FINALIZED_CACHE_TTL_MS = 10 * 60 * 1000;

export const clearSCRV2ReportListCache = () => {
  reportListCache.clear();
};

export const clearSCRV2SummaryReportCache = (scrId = null) => {
  if (!scrId) {
    summaryReportCache.clear();
    return;
  }

  const normalizedScrId = String(scrId).toLowerCase();
  summaryReportCache.delete(normalizedScrId);
};

export const clearSCRV2DetailReportCache = (scrId = null) => {
  if (!scrId) {
    detailReportCache.clear();
    detailReportInFlight.clear();
    clearSCRV2InvoiceDetailCache();
    return;
  }

  const normalizedScrId = String(scrId).toLowerCase();
  detailReportCache.delete(normalizedScrId);
  detailReportInFlight.delete(normalizedScrId);
  clearSCRV2InvoiceDetailCache(scrId);
};

export const clearSCRV2InvoiceDetailCache = (scrId = null) => {
  if (!scrId) {
    invoiceDetailCache.clear();
    invoiceDetailInFlight.clear();
    return;
  }

  const normalizedScrId = `${String(scrId).toLowerCase()}:`;
  [...invoiceDetailCache.keys()].forEach((key) => {
    if (key.startsWith(normalizedScrId)) {
      invoiceDetailCache.delete(key);
    }
  });
  [...invoiceDetailInFlight.keys()].forEach((key) => {
    if (key.startsWith(normalizedScrId)) {
      invoiceDetailInFlight.delete(key);
    }
  });
};

export const invalidateSCRV2CachesForScr = (scrId, { clearReportList = true } = {}) => {
  if (!scrId) {
    return;
  }

  clearSCRV2SummaryReportCache(scrId);
  clearSCRV2DetailReportCache(scrId);

  if (clearReportList) {
    clearSCRV2ReportListCache();
  }
};

/** Clears all SCR V2 report caches when the affected SCR id cannot be resolved. */
export const invalidateAllSCRV2ReportCaches = () => {
  clearSCRV2ReportListCache();
  clearSCRV2SummaryReportCache();
  clearSCRV2DetailReportCache();
};

const buildReportListCacheKey = (request) => JSON.stringify(request ?? {});

const getSummaryCacheTtl = (payload) =>
  payload?.isFinalized ? SUMMARY_REPORT_FINALIZED_CACHE_TTL_MS : SUMMARY_REPORT_LIVE_CACHE_TTL_MS;

const getDetailCacheTtl = (payload) =>
  payload?.isFinalized ? DETAIL_REPORT_FINALIZED_CACHE_TTL_MS : DETAIL_REPORT_LIVE_CACHE_TTL_MS;

const buildInvoiceDetailCacheKey = (scrId, invoiceId) =>
  `${String(scrId ?? "").toLowerCase()}:${String(invoiceId ?? "").toLowerCase()}`;

const getInvoiceDetailCacheTtl = (entry) =>
  entry?.isFinalized ? INVOICE_DETAIL_FINALIZED_CACHE_TTL_MS : INVOICE_DETAIL_LIVE_CACHE_TTL_MS;

const getCachedInvoiceDetail = (scrId, invoiceId) => {
  const cacheKey = buildInvoiceDetailCacheKey(scrId, invoiceId);
  const cached = invoiceDetailCache.get(cacheKey);
  if (!cached) {
    return null;
  }

  if (Date.now() - cached.cachedAt > getInvoiceDetailCacheTtl(cached)) {
    invoiceDetailCache.delete(cacheKey);
    return null;
  }

  return cached.payload;
};

export const peekSCRV2InvoiceDetailCache = (scrId, invoiceId) =>
  getCachedInvoiceDetail(scrId, invoiceId);

const normalizeTransactionItem = (item = {}) => ({
  id: pickValue(item, "id", "Id"),
  transactionCategory: pickValue(item, "transactionCategory", "TransactionCategory", ""),
  shopOrderId: pickValue(item, "shopOrderId", "ShopOrderId"),
  vizShopCode: pickValue(item, "vizShopCode", "VizShopCode", ""),
  shopName: pickValue(item, "shopName", "ShopName", ""),
  invoiceNo: pickValue(item, "invoiceNo", "InvoiceNo", ""),
  invoiceDate: pickValue(item, "invoiceDate", "InvoiceDate"),
  invoiceAmount: Number(pickValue(item, "invoiceAmount", "InvoiceAmount", 0)),
  amount: Number(pickValue(item, "amount", "Amount", 0)),
  paymentDate: pickValue(item, "paymentDate", "PaymentDate"),
  dueDate: pickValue(item, "dueDate", "DueDate"),
  paymentTypeMethod: pickValue(item, "paymentTypeMethod", "PaymentTypeMethod", ""),
  transactionDetail: pickValue(item, "transactionDetail", "TransactionDetail", ""),
  chequeNo: pickValue(item, "chequeNo", "ChequeNo", ""),
  chequeDate: pickValue(item, "chequeDate", "ChequeDate"),
  payerName: pickValue(item, "payerName", "PayerName", ""),
  payerBankId: pickValue(item, "payerBankId", "PayerBankId"),
  bankName: pickValue(item, "bankName", "BankName", ""),
  filePath: pickValue(item, "filePath", "FilePath", ""),
  comments: pickValue(item, "comments", "Comments", ""),
  isRecovery: Boolean(pickValue(item, "isRecovery", "IsRecovery", false)),
  isReversed: Boolean(pickValue(item, "isReversed", "IsReversed", false)),
  reversalDate: pickValue(item, "reversalDate", "ReversalDate"),
  bankDepositId: pickValue(item, "bankDepositId", "BankDepositId"),
  sourceTransactionId: pickValue(item, "sourceTransactionId", "SourceTransactionId"),
  dynamicData: pickValue(item, "dynamicData", "DynamicData"),
});

const normalizeScrInvoiceDetailTransaction = (item = {}) => ({
  id: pickValue(item, "id", "Id"),
  transactionCategory: pickValue(item, "transactionCategory", "TransactionCategory", ""),
  amount: Number(pickValue(item, "amount", "Amount", 0)),
  paymentDate: pickValue(item, "paymentDate", "PaymentDate"),
  dueDate: pickValue(item, "dueDate", "DueDate"),
  paymentTypeMethod: pickValue(item, "paymentTypeMethod", "PaymentTypeMethod", ""),
  transactionDetail: pickValue(item, "transactionDetail", "TransactionDetail", ""),
  filePath: pickValue(item, "filePath", "FilePath", ""),
  comments: pickValue(item, "comments", "Comments", ""),
  isRecovery: Boolean(pickValue(item, "isRecovery", "IsRecovery", false)),
  isReversed: Boolean(pickValue(item, "isReversed", "IsReversed", false)),
  reversalDate: pickValue(item, "reversalDate", "ReversalDate"),
  bankDepositId: pickValue(item, "bankDepositId", "BankDepositId"),
  sourceTransactionId: pickValue(item, "sourceTransactionId", "SourceTransactionId"),
  dynamicData: pickValue(item, "dynamicData", "DynamicData"),
});

const getParticipationModeLabel = (mode) => {
  switch (Number(mode)) {
    case 1:
      return "Recovery Only";
    case 2:
      return "Invoice Base Only";
    case 3:
      return "Invoice Base + Recovery";
    default:
      return "-";
  }
};

export const normalizeSCRV2InvoiceDetail = (raw) => {
  if (!raw || typeof raw !== "object") {
    return null;
  }

  const totalsRaw = pickValue(raw, "totals", "Totals", {});
  const participationMode = Number(pickValue(raw, "participationMode", "ParticipationMode", 0));
  const normalizeTx = (items) => (items || []).map(normalizeScrInvoiceDetailTransaction);

  const totals = {
    originalInvoiceAmount: Number(
      pickValue(totalsRaw, "originalInvoiceAmount", "OriginalInvoiceAmount", 0)
    ),
    scopedBaseAmount: Number(pickValue(totalsRaw, "scopedBaseAmount", "ScopedBaseAmount", 0)),
    scopedDueAmount: Number(pickValue(totalsRaw, "scopedDueAmount", "ScopedDueAmount", 0)),
    totalVizLinkCollectionAmount: Number(
      pickValue(totalsRaw, "totalVizLinkCollectionAmount", "TotalVizLinkCollectionAmount", 0)
    ),
    totalNonVizLinkCollectionAmount: Number(
      pickValue(totalsRaw, "totalNonVizLinkCollectionAmount", "TotalNonVizLinkCollectionAmount", 0)
    ),
    totalRecoveryAmount: Number(pickValue(totalsRaw, "totalRecoveryAmount", "TotalRecoveryAmount", 0)),
    totalCreditAmount: Number(pickValue(totalsRaw, "totalCreditAmount", "TotalCreditAmount", 0)),
    totalReversalAmount: Number(pickValue(totalsRaw, "totalReversalAmount", "TotalReversalAmount", 0)),
    totalAdjustmentAmount: Number(
      pickValue(totalsRaw, "totalAdjustmentAmount", "TotalAdjustmentAmount", 0)
    ),
    totalBankDepositAmount: Number(
      pickValue(totalsRaw, "totalBankDepositAmount", "TotalBankDepositAmount", 0)
    ),
    varianceAmount: Number(pickValue(totalsRaw, "varianceAmount", "VarianceAmount", 0)),
  };

  totals.scopedReceivedAmount =
    totals.totalVizLinkCollectionAmount +
    totals.totalNonVizLinkCollectionAmount +
    totals.totalRecoveryAmount;

  // ReversalEntries are not listed separately: originals already carry IsReversed + ReversalDate.
  const transactions = [
    ...normalizeTx(pickValue(raw, "vizLinkTransactions", "VizLinkTransactions")),
    ...normalizeTx(pickValue(raw, "nonVizLinkTransactions", "NonVizLinkTransactions")),
    ...normalizeTx(pickValue(raw, "invoiceAdjustments", "InvoiceAdjustments")),
    ...normalizeTx(pickValue(raw, "creditInvoices", "CreditInvoices")),
  ].sort(
    (left, right) =>
      new Date(left.paymentDate || 0).getTime() - new Date(right.paymentDate || 0).getTime()
  );

  return {
    scrId: pickValue(raw, "scrId", "SCRId"),
    scrNumber: pickValue(raw, "scrNumber", "SCRNumber", ""),
    participationMode,
    participationModeLabel: getParticipationModeLabel(participationMode),
    shopOrderId: pickValue(raw, "shopOrderId", "ShopOrderId"),
    invoiceNumber: pickValue(raw, "invoiceNumber", "InvoiceNumber", ""),
    vizproInvoiceNumber: pickValue(raw, "vizproInvoiceNumber", "VizproInvoiceNumber", ""),
    invoiceDate: pickValue(raw, "invoiceDate", "InvoiceDate"),
    dueDate: pickValue(raw, "dueDate", "DueDate"),
    invoiceAmount: Number(pickValue(raw, "invoiceAmount", "InvoiceAmount", 0)),
    originalInvoiceAmount: Number(pickValue(raw, "originalInvoiceAmount", "OriginalInvoiceAmount", 0)),
    distributorName: pickValue(raw, "distributorName", "DistributorName", ""),
    distCenterName: pickValue(raw, "distCenterName", "DistCenterName", ""),
    totals,
    transactions,
  };
};

export const prefetchSCRV2InvoiceDetail = (scrId, invoiceId, { isFinalized = false } = {}) => {
  if (!scrId || !invoiceId) {
    return Promise.resolve(null);
  }

  if (getCachedInvoiceDetail(scrId, invoiceId)) {
    return Promise.resolve(null);
  }

  return fetchSCRV2InvoiceDetail(scrId, invoiceId, { isFinalized }).catch(() => null);
};

export const fetchSCRV2InvoiceDetail = async (
  scrId,
  invoiceId,
  { useCache = true, forceRefresh = false, isFinalized = false } = {}
) => {
  const normalizedScrId = String(scrId ?? "").trim();
  const normalizedInvoiceId = String(invoiceId ?? "").trim();

  if (!normalizedScrId || !normalizedInvoiceId) {
    throw new Error("SCR id and invoice id are required.");
  }

  if (useCache && !forceRefresh) {
    const cachedPayload = getCachedInvoiceDetail(normalizedScrId, normalizedInvoiceId);
    if (cachedPayload) {
      return cachedPayload;
    }
  }

  const cacheKey = buildInvoiceDetailCacheKey(normalizedScrId, normalizedInvoiceId);
  const inFlightRequest = invoiceDetailInFlight.get(cacheKey);
  if (inFlightRequest) {
    return inFlightRequest;
  }

  const fetchPromise = axios
    .post("api/SCRV2Reports/GetInvoiceDetail", {
      scrId: normalizedScrId,
      SCRId: normalizedScrId,
      invoiceId: normalizedInvoiceId,
      InvoiceId: normalizedInvoiceId,
    })
    .then(({ data }) => {
      const payload = normalizeSCRV2InvoiceDetail(extractApiResponse(data));
      if (!payload?.totals) {
        throw new Error("Invoice detail response was incomplete.");
      }

      invoiceDetailCache.set(cacheKey, {
        payload,
        cachedAt: Date.now(),
        isFinalized: Boolean(isFinalized),
      });

      return payload;
    })
    .finally(() => {
      invoiceDetailInFlight.delete(cacheKey);
    });

  invoiceDetailInFlight.set(cacheKey, fetchPromise);
  return fetchPromise;
};

export const normalizeSCRV2DetailReport = (raw, scrIdFallback = null) => {
  if (!raw || typeof raw !== "object") {
    return raw;
  }

  const totalsRaw = pickValue(raw, "totals", "Totals", {});
  const identity = pickScrV2IdentityFields(raw);

  return {
    scrId: pickValue(raw, "scrId", "SCRId", scrIdFallback),
    scrNo: pickValue(raw, "scrNo", "SCRNo", ""),
    businessDate: identity.businessDate,
    finalizedDate: identity.finalizedDate,
    finalizedAt: identity.finalizedAt,
    durationDays: identity.durationDays,
    isFinalized: identity.isFinalized,
    dlmId: pickValue(raw, "dlmId", "DLMId"),
    dlmUserName: pickValue(raw, "dlmUserName", "DLMUserName", ""),
    dlmName: pickValue(raw, "dlmName", "DLMName", ""),
    deliveryQRCode: pickValue(raw, "deliveryQRCode", "DeliveryQRCode", ""),
    distributorName: pickValue(raw, "distributorName", "DistributorName", ""),
    distCenterName: pickValue(raw, "distCenterName", "DistCenterName", ""),
    totals: {
      totalInvoices: Number(pickValue(totalsRaw, "totalInvoices", "TotalInvoices", 0)),
      totalOriginalInvoicesAmount: Number(
        pickValue(totalsRaw, "totalOriginalInvoicesAmount", "TotalOriginalInvoicesAmount", 0)
      ),
      totalInvoicesAmount: Number(pickValue(totalsRaw, "totalInvoicesAmount", "TotalInvoicesAmount", 0)),
      totalCreditAmount: Number(pickValue(totalsRaw, "totalCreditAmount", "TotalCreditAmount", 0)),
      totalRecoveryAmount: Number(pickValue(totalsRaw, "totalRecoveryAmount", "TotalRecoveryAmount", 0)),
      totalVizLinkCollectionAmount: Number(
        pickValue(totalsRaw, "totalVizLinkCollectionAmount", "TotalVizLinkCollectionAmount", 0)
      ),
      totalNonVizLinkCollectionAmount: Number(
        pickValue(totalsRaw, "totalNonVizLinkCollectionAmount", "TotalNonVizLinkCollectionAmount", 0)
      ),
      totalAdjustmentAmount: Number(
        pickValue(totalsRaw, "totalAdjustmentAmount", "TotalAdjustmentAmount", 0)
      ),
      totalReversalAmount: Number(pickValue(totalsRaw, "totalReversalAmount", "TotalReversalAmount", 0)),
      totalBankDepositAmount: Number(
        pickValue(totalsRaw, "totalBankDepositAmount", "TotalBankDepositAmount", 0)
      ),
      varianceAmount: Number(pickValue(totalsRaw, "varianceAmount", "VarianceAmount", 0)),
    },
    vizLinkTransactions: (
      pickValue(raw, "vizLinkTransactions", "VizLinkTransactions", []) || []
    ).map(normalizeTransactionItem),
    nonVizLinkTransactions: (
      pickValue(raw, "nonVizLinkTransactions", "NonVizLinkTransactions", []) || []
    ).map(normalizeTransactionItem),
    reversalEntries: (pickValue(raw, "reversalEntries", "ReversalEntries", []) || []).map(
      normalizeTransactionItem
    ),
    invoiceAdjustments: (
      pickValue(raw, "invoiceAdjustments", "InvoiceAdjustments", []) || []
    ).map(normalizeTransactionItem),
    creditInvoices: (pickValue(raw, "creditInvoices", "CreditInvoices", []) || []).map(
      normalizeTransactionItem
    ),
    // Summary navigation seeds totals-only previews; never treat those as a loaded Detailed Report.
    isDetailPreview: Boolean(pickValue(raw, "isDetailPreview", "IsDetailPreview", false)),
  };
};

export const isSCRV2DetailComplete = (report) =>
  Boolean(
    report?.scrId &&
      report?.totals &&
      !report.isDetailPreview &&
      Array.isArray(report?.vizLinkTransactions) &&
      Array.isArray(report?.nonVizLinkTransactions) &&
      Array.isArray(report?.invoiceAdjustments) &&
      Array.isArray(report?.creditInvoices)
  );

export const buildSCRV2DetailPreviewFromSummary = (summary = {}) =>
  normalizeSCRV2DetailReport(
    {
      scrId: summary.scrId,
      scrNo: summary.scrNo,
      businessDate: summary.businessDate,
      finalizedDate: summary.finalizedDate,
      finalizedAt: summary.finalizedAt,
      durationDays: summary.durationDays,
      isFinalized: summary.isFinalized,
      dlmName: summary.dlmName,
      dlmUserName: summary.dlmUserName,
      deliveryQRCode: summary.deliveryQRCode,
      distributorName: summary.distributorName,
      distCenterName: summary.distCenterName,
      totals: {
        totalInvoices: summary.highlights?.totalInvoices,
        totalOriginalInvoicesAmount: summary.paymentDue?.totalOriginalInvoicesAmount,
        totalInvoicesAmount: summary.paymentDue?.totalInvoicesAmount,
        totalCreditAmount: summary.paymentDue?.totalCreditAmount,
        totalRecoveryAmount: summary.paymentDue?.totalRecoveryAmount,
        totalAdjustmentAmount: summary.paymentDue?.totalAdjustmentAmount,
        totalReversalAmount: summary.reversals?.totalReversalAmount,
        totalVizLinkCollectionAmount: summary.paymentReceived?.totalVizLinkCollectionAmount,
        totalNonVizLinkCollectionAmount: summary.paymentReceived?.totalNonVizLinkCollectionAmount,
        varianceAmount: summary.highlights?.varianceAmount,
      },
      vizLinkTransactions: [],
      nonVizLinkTransactions: [],
      reversalEntries: [],
      invoiceAdjustments: [],
      creditInvoices: [],
      isDetailPreview: true,
    },
    summary.scrId
  );

const isValidScrId = (scrId) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    String(scrId ?? "").trim()
  );

const buildSummaryReportRequestBody = (scrId) => ({
  scrId,
  SCRId: scrId,
});

const normalizeBreakdownItem = (item = {}) => ({
  paymentType: pickValue(item, "paymentType", "PaymentType", ""),
  amount: Number(pickValue(item, "amount", "Amount", 0)),
  transactionCount: Number(pickValue(item, "transactionCount", "TransactionCount", 0)),
});

export const normalizeSCRV2SummaryReport = (raw, scrIdFallback = null) => {
  if (!raw || typeof raw !== "object") {
    return raw;
  }

  const highlightsRaw = pickValue(raw, "highlights", "Highlights", {});
  const paymentDueRaw = pickValue(raw, "paymentDue", "PaymentDue", {});
  const paymentReceivedRaw = pickValue(raw, "paymentReceived", "PaymentReceived", {});
  const reversalsRaw = pickValue(raw, "reversals", "Reversals", {});
  const finalizationRaw = pickValue(raw, "finalization", "Finalization", {});

  const scrId = pickValue(raw, "scrId", "SCRId", scrIdFallback);
  const identity = pickScrV2IdentityFields(raw);

  return {
    scrId,
    scrNo: pickValue(raw, "scrNo", "SCRNo", ""),
    businessDate: identity.businessDate,
    finalizedDate: identity.finalizedDate,
    finalizedAt: identity.finalizedAt,
    durationDays: identity.durationDays,
    isFinalized: identity.isFinalized,
    dlmUserName: pickValue(raw, "dlmUserName", "DLMUserName", ""),
    dlmName: pickValue(raw, "dlmName", "DLMName", ""),
    deliveryQRCode: pickValue(raw, "deliveryQRCode", "DeliveryQRCode", ""),
    distributorName: pickValue(raw, "distributorName", "DistributorName", ""),
    distCenterName: pickValue(raw, "distCenterName", "DistCenterName", ""),
    highlights: {
      totalInvoices: Number(pickValue(highlightsRaw, "totalInvoices", "TotalInvoices", 0)),
      totalReceived: Number(pickValue(highlightsRaw, "totalReceived", "TotalReceived", 0)),
      collectionRatePercent: Number(
        pickValue(highlightsRaw, "collectionRatePercent", "CollectionRatePercent", 0)
      ),
      varianceAmount: Number(pickValue(highlightsRaw, "varianceAmount", "VarianceAmount", 0)),
    },
    paymentDue: {
      totalOriginalInvoicesAmount: Number(
        pickValue(paymentDueRaw, "totalOriginalInvoicesAmount", "TotalOriginalInvoicesAmount", 0)
      ),
      totalInvoicesAmount: Number(
        pickValue(paymentDueRaw, "totalInvoicesAmount", "TotalInvoicesAmount", 0)
      ),
      totalAdjustmentAmount: Number(
        pickValue(paymentDueRaw, "totalAdjustmentAmount", "TotalAdjustmentAmount", 0)
      ),
      totalCreditAmount: Number(pickValue(paymentDueRaw, "totalCreditAmount", "TotalCreditAmount", 0)),
      totalRecoveryAmount: Number(
        pickValue(paymentDueRaw, "totalRecoveryAmount", "TotalRecoveryAmount", 0)
      ),
      grandTotalDue: Number(pickValue(paymentDueRaw, "grandTotalDue", "GrandTotalDue", 0)),
    },
    paymentReceived: {
      totalVizLinkCollectionAmount: Number(
        pickValue(
          paymentReceivedRaw,
          "totalVizLinkCollectionAmount",
          "TotalVizLinkCollectionAmount",
          0
        )
      ),
      totalNonVizLinkCollectionAmount: Number(
        pickValue(
          paymentReceivedRaw,
          "totalNonVizLinkCollectionAmount",
          "TotalNonVizLinkCollectionAmount",
          0
        )
      ),
      nonVizLinkAmountInHand: Number(
        pickValue(paymentReceivedRaw, "nonVizLinkAmountInHand", "NonVizLinkAmountInHand", 0)
      ),
      nonVizLinkAmountDepositedInBank: Number(
        pickValue(
          paymentReceivedRaw,
          "nonVizLinkAmountDepositedInBank",
          "NonVizLinkAmountDepositedInBank",
          0
        )
      ),
      nonVizLinkInHandTransactionCount: Number(
        pickValue(
          paymentReceivedRaw,
          "nonVizLinkInHandTransactionCount",
          "NonVizLinkInHandTransactionCount",
          0
        )
      ),
      nonVizLinkDepositedTransactionCount: Number(
        pickValue(
          paymentReceivedRaw,
          "nonVizLinkDepositedTransactionCount",
          "NonVizLinkDepositedTransactionCount",
          0
        )
      ),
      amountCollected: Number(
        pickValue(paymentReceivedRaw, "amountCollected", "AmountCollected", 0)
      ),
      amountCollectedTransactionCount: Number(
        pickValue(
          paymentReceivedRaw,
          "amountCollectedTransactionCount",
          "AmountCollectedTransactionCount",
          0
        )
      ),
      vizLinkBreakdown: (
        pickValue(paymentReceivedRaw, "vizLinkBreakdown", "VizLinkBreakdown", []) || []
      ).map(normalizeBreakdownItem),
      nonVizLinkBreakdown: (
        pickValue(paymentReceivedRaw, "nonVizLinkBreakdown", "NonVizLinkBreakdown", []) || []
      ).map(normalizeBreakdownItem),
      recoveryBreakdown: (
        pickValue(paymentReceivedRaw, "recoveryBreakdown", "RecoveryBreakdown", []) || []
      ).map(normalizeBreakdownItem),
    },
    reversals: {
      totalReversalAmount: Number(
        pickValue(reversalsRaw, "totalReversalAmount", "TotalReversalAmount", 0)
      ),
      reversalCount: Number(pickValue(reversalsRaw, "reversalCount", "ReversalCount", 0)),
    },
    finalization: {
      canFinalize: Boolean(pickValue(finalizationRaw, "canFinalize", "CanFinalize", false)),
      blockingReasons:
        pickValue(finalizationRaw, "blockingReasons", "BlockingReasons", []) || [],
      pendingInvoiceCount: Number(
        pickValue(finalizationRaw, "pendingInvoiceCount", "PendingInvoiceCount", 0)
      ),
      partialInvoiceCount: Number(
        pickValue(finalizationRaw, "partialInvoiceCount", "PartialInvoiceCount", 0)
      ),
      creditInvoiceCount: Number(
        pickValue(finalizationRaw, "creditInvoiceCount", "CreditInvoiceCount", 0)
      ),
      blockingInvoices: normalizeSCRV2BlockingInvoices(
        pickValue(finalizationRaw, "blockingInvoices", "BlockingInvoices", []) || []
      ),
    },
  };
};

const normalizeSCRV2BlockingInvoices = (rawInvoices) => {
  if (!Array.isArray(rawInvoices)) {
    return [];
  }

  return rawInvoices.map((invoice) => ({
    invoiceNumber: String(
      pickValue(invoice, "invoiceNumber", "InvoiceNumber", "") || ""
    ),
    // Pass through backend OutstandingAmount only — do not recompute from NetSales/AmountPaid.
    outstandingAmount: Number(
      pickValue(invoice, "outstandingAmount", "OutstandingAmount", 0)
    ),
  }));
};

export { normalizeSCRV2BlockingInvoices };

const getCachedReportList = (request) => {
  const cacheKey = buildReportListCacheKey(request);
  const cached = reportListCache.get(cacheKey);
  if (!cached) {
    return null;
  }

  if (Date.now() - cached.cachedAt > REPORT_LIST_CACHE_TTL_MS) {
    reportListCache.delete(cacheKey);
    return null;
  }

  return cached.payload;
};

const getCachedSummaryReport = (scrId) => {
  const cacheKey = String(scrId ?? "").toLowerCase();
  const cached = summaryReportCache.get(cacheKey);
  if (!cached) {
    return null;
  }

  if (Date.now() - cached.cachedAt > getSummaryCacheTtl(cached.payload)) {
    summaryReportCache.delete(cacheKey);
    return null;
  }

  if (!isSCRV2SummaryComplete(cached.payload)) {
    summaryReportCache.delete(cacheKey);
    return null;
  }

  return cached.payload;
};

export const peekSCRV2SummaryReportCache = (scrId) => getCachedSummaryReport(scrId);

const getCachedDetailReport = (scrId) => {
  const cacheKey = String(scrId ?? "").toLowerCase();
  const cached = detailReportCache.get(cacheKey);
  if (!cached) {
    return null;
  }

  if (Date.now() - cached.cachedAt > getDetailCacheTtl(cached.payload)) {
    detailReportCache.delete(cacheKey);
    return null;
  }

  if (!isSCRV2DetailComplete(cached.payload)) {
    detailReportCache.delete(cacheKey);
    return null;
  }

  return cached.payload;
};

export const peekSCRV2DetailReportCache = (scrId) => getCachedDetailReport(scrId);

export const normalizeSCRV2ReportListItem = (raw = {}) => {
  const identity = pickScrV2IdentityFields(raw);
  return {
    scrId: pickValue(raw, "scrId", "SCRId"),
    scrNo: pickValue(raw, "scrNo", "SCRNo", ""),
    businessDate: identity.businessDate,
    finalizedDate: identity.finalizedDate,
    finalizedAt: identity.finalizedAt,
    durationDays: identity.durationDays,
    isFinalized: identity.isFinalized,
    dlmUserName: pickValue(raw, "dlmUserName", "DLMUserName", ""),
    dlmName: pickValue(raw, "dlmName", "DLMName", ""),
    distCenterName: pickValue(raw, "distCenterName", "DistCenterName", ""),
    distributorName: pickValue(raw, "distributorName", "DistributorName", ""),
  };
};

export const normalizeSCRV2ReportListResponse = (raw = {}) => {
  const items = pickValue(raw, "items", "Items", []) || [];
  return {
    totalRecords: Number(pickValue(raw, "totalRecords", "TotalRecords", 0)),
    pageNumber: Number(pickValue(raw, "pageNumber", "PageNumber", 1)),
    pageSize: Number(pickValue(raw, "pageSize", "PageSize", 20)),
    items: items.map(normalizeSCRV2ReportListItem),
  };
};

export const buildSCRV2SummaryPreview = (source = {}) =>
  normalizeSCRV2SummaryReport(
    {
      scrId: source.scrId ?? source.SCRId,
      scrNo: source.scrNo ?? source.SCRNo,
      businessDate: source.businessDate ?? source.BusinessDate,
      finalizedDate: source.finalizedDate ?? source.FinalizedDate,
      finalizedAt: source.finalizedAt ?? source.FinalizedAt,
      durationDays: source.durationDays ?? source.DurationDays,
      isFinalized: source.isFinalized ?? source.IsFinalized,
      dlmName: source.dlmName ?? source.DLMName,
      dlmUserName: source.dlmUserName ?? source.DLMUserName,
      deliveryQRCode: source.deliveryQRCode ?? source.DeliveryQRCode,
      distributorName: source.distributorName ?? source.DistributorName,
      distCenterName: source.distCenterName ?? source.DistCenterName,
    },
    source.scrId ?? source.SCRId
  );

export const isSCRV2SummaryComplete = (report) =>
  Boolean(
    report?.scrId &&
      report?.highlights &&
      report?.paymentDue &&
      report?.paymentReceived &&
      report?.reversals &&
      report?.finalization
  );

export const normalizeFinalizeResponse = (raw, scrIdFallback = null) => {
  const identity = pickScrV2IdentityFields(raw);
  return {
    scrId: pickValue(raw, "scrId", "SCRId", scrIdFallback),
    scrNo: pickValue(raw, "scrNo", "SCRNo", ""),
    isFinalized: Boolean(pickValue(raw, "isFinalized", "IsFinalized", true)),
    finalizedAt: identity.finalizedAt,
    finalizedDate: identity.finalizedDate,
    durationDays: identity.durationDays,
    varianceAmount: Number(pickValue(raw, "varianceAmount", "VarianceAmount", 0)),
  };
};

export const applyFinalizeToSummary = (report, finalizeResponse) => {
  const normalizedReport = normalizeSCRV2SummaryReport(report, report?.scrId);
  const finalized = normalizeFinalizeResponse(finalizeResponse, normalizedReport?.scrId);

  return normalizeSCRV2SummaryReport(
    {
      ...normalizedReport,
      isFinalized: true,
      scrNo: finalized.scrNo || normalizedReport.scrNo,
      finalizedAt: finalized.finalizedAt ?? normalizedReport.finalizedAt,
      finalizedDate: finalized.finalizedDate ?? normalizedReport.finalizedDate,
      durationDays: finalized.durationDays ?? normalizedReport.durationDays,
      highlights: {
        ...normalizedReport.highlights,
        varianceAmount:
          finalized.varianceAmount ?? normalizedReport.highlights?.varianceAmount,
      },
      finalization: {
        ...normalizedReport.finalization,
        canFinalize: false,
        blockingReasons: [],
        blockingInvoices: [],
        pendingInvoiceCount: 0,
        partialInvoiceCount: 0,
      },
    },
    normalizedReport.scrId
  );
};

let summaryReportPrefetchAbortController = null;
let finalizeReportAbortController = null;

export const prefetchSCRV2SummaryReport = (scrId) => {
  if (!isValidScrId(scrId) || getCachedSummaryReport(scrId)) {
    return Promise.resolve(null);
  }

  if (summaryReportPrefetchAbortController) {
    summaryReportPrefetchAbortController.abort();
  }

  summaryReportPrefetchAbortController = new AbortController();

  return fetchSCRV2SummaryReport(scrId, {
    signal: summaryReportPrefetchAbortController.signal,
  }).catch(() => null);
};

export const prefetchSCRV2CompleteDetail = (scrId) => {
  if (!isValidScrId(scrId) || getCachedDetailReport(scrId)) {
    return Promise.resolve(null);
  }

  return fetchSCRV2CompleteDetailReport(scrId).catch(() => null);
};

const buildDetailReportRequestBody = (scrId) => ({
  scrId,
  SCRId: scrId,
});

export const fetchSCRV2CompleteDetailReport = async (
  scrId,
  { useCache = true, forceRefresh = false, signal } = {}
) => {
  const normalizedScrId = String(scrId ?? "").toLowerCase();
  if (!isValidScrId(normalizedScrId)) {
    throw new Error("SCR id is required.");
  }

  if (useCache && !forceRefresh) {
    const cachedPayload = getCachedDetailReport(scrId);
    if (cachedPayload) {
      return cachedPayload;
    }
  }

  if (detailReportInFlight.has(normalizedScrId)) {
    return detailReportInFlight.get(normalizedScrId);
  }

  const fetchPromise = (async () => {
    let requestSignal = signal;
    if (!requestSignal) {
      if (
        detailReportAbortController &&
        detailReportActiveScrId &&
        detailReportActiveScrId !== normalizedScrId
      ) {
        detailReportAbortController.abort();
      }

      detailReportAbortController = new AbortController();
      detailReportActiveScrId = normalizedScrId;
      requestSignal = detailReportAbortController.signal;
    }

    const { data } = await axios.post(
      "api/SCRV2Reports/GetDetailedReport",
      buildDetailReportRequestBody(scrId),
      { signal: requestSignal }
    );

    const payload = normalizeSCRV2DetailReport(extractApiResponse(data), scrId);
    if (!isSCRV2DetailComplete(payload)) {
      throw new Error("Detailed report response was incomplete.");
    }

    detailReportCache.set(normalizedScrId, {
      payload,
      cachedAt: Date.now(),
    });

    return payload;
  })().finally(() => {
    detailReportInFlight.delete(normalizedScrId);
  });

  detailReportInFlight.set(normalizedScrId, fetchPromise);
  return fetchPromise;
};

export const fetchSCRV2SummaryReport = async (
  scrId,
  { useCache = true, forceRefresh = false, signal } = {}
) => {
  const normalizedScrId = String(scrId ?? "").toLowerCase();
  if (!isValidScrId(normalizedScrId)) {
    throw new Error("SCR id is required.");
  }

  if (useCache && !forceRefresh) {
    const cachedPayload = getCachedSummaryReport(scrId);
    if (cachedPayload) {
      return cachedPayload;
    }
  }

  let requestSignal = signal;
  if (!requestSignal) {
    if (
      summaryReportAbortController &&
      summaryReportActiveScrId &&
      summaryReportActiveScrId !== normalizedScrId
    ) {
      summaryReportAbortController.abort();
    }

    summaryReportAbortController = new AbortController();
    summaryReportActiveScrId = normalizedScrId;
    requestSignal = summaryReportAbortController.signal;
  }

  const { data } = await axios.post(
    "api/SCRV2Reports/GetSummaryReport",
    buildSummaryReportRequestBody(scrId),
    { signal: requestSignal }
  );

  const payload = normalizeSCRV2SummaryReport(extractApiResponse(data), scrId);
  if (!isSCRV2SummaryComplete(payload)) {
    throw new Error("Summary report response was incomplete.");
  }

  summaryReportCache.set(normalizedScrId, {
    payload,
    cachedAt: Date.now(),
  });

  return payload;
};

export const getSCRV2ReportList = (request) => async (dispatch) => {
  const cachedPayload = getCachedReportList(request);
  if (cachedPayload) {
    dispatch({
      type: GET_SCR_V2_REPORT_LIST,
      payload: cachedPayload,
    });
    return;
  }

  if (reportListAbortController) {
    reportListAbortController.abort();
  }

  reportListAbortController = new AbortController();
  const { signal } = reportListAbortController;

  dispatch(showLoading());

  try {
    const { data } = await axios.post("api/SCRV2Reports/GetReportList", request, { signal });

    const normalizedList = normalizeSCRV2ReportListResponse(data.response);

    reportListCache.set(buildReportListCacheKey(request), {
      payload: normalizedList,
      cachedAt: Date.now(),
    });

    dispatch({
      type: GET_SCR_V2_REPORT_LIST,
      payload: normalizedList,
    });
  } catch (error) {
    handleRequestError(error);
  } finally {
    if (!signal.aborted) {
      dispatch(hideLoading());
    }
  }
};

export const getSCRV2CompleteDetail = (request) => async (dispatch) => {
  const scrId = request?.scrId ?? request?.SCRId;
  const cachedPayload = scrId ? getCachedDetailReport(scrId) : null;
  const normalizedScrId = String(scrId ?? "").toLowerCase();

  if (cachedPayload) {
    dispatch({
      type: GET_SCR_V2_COMPLETE_DETAIL,
      payload: cachedPayload,
    });

    if (!cachedPayload.isFinalized) {
      fetchSCRV2CompleteDetailReport(scrId, { forceRefresh: true })
        .then((payload) => {
          dispatch({
            type: GET_SCR_V2_COMPLETE_DETAIL,
            payload,
          });
        })
        .catch((error) => {
          if (!axios.isCancel?.(error) && error?.code !== "ERR_CANCELED") {
            handleRequestError(error);
          }
        });
    }

    return cachedPayload;
  }

  const showGlobalLoading = !request?.silent;

  try {
    if (showGlobalLoading) {
      dispatch(showLoading());
    }

    const payload = await fetchSCRV2CompleteDetailReport(scrId);
    dispatch({
      type: GET_SCR_V2_COMPLETE_DETAIL,
      payload,
    });
    return payload;
  } catch (error) {
    if (!axios.isCancel?.(error) && error?.code !== "ERR_CANCELED") {
      handleRequestError(error);
    }
    throw error;
  } finally {
    if (showGlobalLoading && detailReportActiveScrId === normalizedScrId) {
      dispatch(hideLoading());
    }
  }
};

export const getSCRV2SummaryReport = (request) => async (dispatch) => {
  const scrId = request?.scrId;
  const cachedPayload = scrId ? getCachedSummaryReport(scrId) : null;

  if (cachedPayload) {
    const normalizedPayload = normalizeSCRV2SummaryReport(cachedPayload, scrId);
    dispatch({
      type: GET_SCR_V2_SUMMARY_REPORT,
      payload: normalizedPayload,
    });

    if (!normalizedPayload.isFinalized) {
      fetchSCRV2SummaryReport(scrId, { forceRefresh: true })
        .then((payload) => {
          dispatch({
            type: GET_SCR_V2_SUMMARY_REPORT,
            payload,
          });
        })
        .catch((error) => {
          if (!axios.isCancel?.(error) && error?.code !== "ERR_CANCELED") {
            handleRequestError(error);
          }
        });
    }

    return normalizedPayload;
  }

  try {
    const payload = await fetchSCRV2SummaryReport(scrId);
    dispatch({
      type: GET_SCR_V2_SUMMARY_REPORT,
      payload,
    });
    return payload;
  } catch (error) {
    handleRequestError(error);
    throw error;
  }
};

export const finalizeSCRV2SummaryReport = (request) => async (dispatch) => {
  if (finalizeReportAbortController) {
    finalizeReportAbortController.abort();
  }

  finalizeReportAbortController = new AbortController();
  const { signal } = finalizeReportAbortController;

  try {
    const { data } = await axios.post(
      "api/SCRV2Reports/FinalizeReport",
      buildSummaryReportRequestBody(request.scrId),
      { signal }
    );

    const finalizePayload = normalizeFinalizeResponse(
      extractApiResponse(data),
      request.scrId
    );
    const mergedSummary = applyFinalizeToSummary(request.currentReport ?? { scrId: request.scrId }, finalizePayload);

    clearSCRV2ReportListCache();
    clearSCRV2SummaryReportCache(request.scrId);
    clearSCRV2DetailReportCache(request.scrId);
    summaryReportCache.set(String(request.scrId).toLowerCase(), {
      payload: mergedSummary,
      cachedAt: Date.now(),
    });

    dispatch({
      type: GET_SCR_V2_SUMMARY_REPORT,
      payload: mergedSummary,
    });

    toast.success(data?.message || data?.Message || "SCR finalized successfully");
    return mergedSummary;
  } catch (error) {
    if (!axios.isCancel?.(error) && error?.code !== "ERR_CANCELED") {
      handleRequestError(error);
    }

    throw error;
  }
};

export const buildSCRV2ReportListExportRequest = ({
  fromDate = null,
  toDate = null,
  isFinalized = null,
  scrNo = null,
  distributorIds = null,
  distCenterIds = null,
  dlmIds = null,
  deliveryQRCodes = null,
  searchText = null,
} = {}) => ({
  fromDate,
  toDate,
  isFinalized,
  scrNo,
  distributorIds,
  distCenterIds,
  dlmIds,
  deliveryQRCodes,
  searchText,
});

export const exportSCRV2InvoiceWiseDetailList = async (request) => {
  try {
    const { data, headers } = await axios.post(
      "api/SCRV2Reports/ExportInvoiceWiseDetailList",
      buildSCRV2ReportListExportRequest(request),
      { responseType: "blob" }
    );

    const contentDisposition = headers?.["content-disposition"] || headers?.["Content-Disposition"] || "";
    const fileNameMatch = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
    const fileName = fileNameMatch?.[1]
      ? fileNameMatch[1].replace(/['"]/g, "")
      : `SCRV2InvoiceWiseDetail-List-${new Date().toISOString().slice(0, 10).replace(/-/g, "")}.xlsx`;

    const blob = new Blob([data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });
    fileSaver.saveAs(blob, fileName);
    toast.info("Invoice Wise Detail list exported successfully", { position: "bottom-right" });
  } catch (error) {
    if (error?.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        const parsed = JSON.parse(text);
        toast.error(parsed?.message || parsed?.Message || "Export failed.");
        return;
      } catch {
        // fall through
      }
    }

    handleRequestError(error);
  }
};

export const exportSCRV2InvoiceWiseDetail = async (scrId) => {
  if (!scrId) {
    toast.error("SCR id is required for export.");
    return;
  }

  try {
    const { data, headers } = await axios.post(
      "api/SCRV2Reports/ExportInvoiceWiseDetail",
      { scrId },
      { responseType: "blob" }
    );

    const contentDisposition = headers?.["content-disposition"] || headers?.["Content-Disposition"] || "";
    const fileNameMatch = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
    const fileName = fileNameMatch?.[1]
      ? fileNameMatch[1].replace(/['"]/g, "")
      : `SCRV2InvoiceWiseDetail-${scrId}.xlsx`;

    const blob = new Blob([data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });
    fileSaver.saveAs(blob, fileName);
    toast.info("Invoice Wise Detail exported successfully", { position: "bottom-right" });
  } catch (error) {
    if (error?.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        const parsed = JSON.parse(text);
        toast.error(parsed?.message || parsed?.Message || "Export failed.");
        return;
      } catch {
        // fall through
      }
    }

    handleRequestError(error);
  }
};
